import moment from "moment";
import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { TimeEnum } from "../lib/enums";
import { Timerange, TimerangeFilter } from "../modules/search/@types";
import { ISO_YEAR_MONTH_DAY_FORMAT_UTC } from "../modules/search/consts";

import { formatDate, utcTimestampFromString } from "../utils";

import CustomTimerange from "./CustomTimerange";
import DropdownFilter from "./DropdownFilter";
import TabDropdownMenu from "./TabDropdownMenu";

export type MenuItem = {
  label: string;
  value: string;
  content?: ({ closeMenu }: { closeMenu: () => void }) => JSX.Element;
};

interface ITimeRangeDropdownFilter {
  activeFilter: string;
  allTitle?: string;
  buttonClassname?: string;
  className?: string;
  customTimeRange: Timerange | undefined;
  disabled: boolean;
  styles?: { [key: string]: React.CSSProperties };
  title?: string;
  onlyPeriods: boolean;
  withoutIcon: boolean;
  onTimerangeChange: (timerange: Timerange, filter: TimerangeFilter) => void;
  onFilterChange?: (filter: TimerangeFilter) => void;
  getMenuItems?: (
    allMenuItem: MenuItem,
    customDateMenuItem: MenuItem
  ) => MenuItem[];
  type?: "time";
  customMenuStyles?: {
    menuWithContentWidth: string | number;
    menuColumnSize: number;
    contentColumnSize: number;
  };
  isInvalid?: boolean;
  isSynced?: boolean;
}

const getMenuItems = (allMenuItem: MenuItem, customDateMenuItem: MenuItem) => [
  allMenuItem,
  customDateMenuItem,
  {
    label: "Today",
    value: TimeEnum.TODAY
  },
  {
    label: "Last Week",
    value: TimeEnum.LAST_WEEK
  },
  {
    label: "Last Month",
    value: TimeEnum.LAST_MONTH
  },
  {
    label: "Last 3 Months",
    value: TimeEnum.LAST_QUARTER
  },
  {
    label: "Last Year",
    value: TimeEnum.LAST_YEAR
  }
];

const TimeRangeDropdownFilter: React.FC<ITimeRangeDropdownFilter> = props => {
  const [timerange, setTimerange] = useState<Timerange | null>(null);

  useEffect(() => {
    if (props.customTimeRange) {
      setTimerange(props.customTimeRange);
    }
  }, [props.customTimeRange]);

  const formatStart = (start: Date) => {
    return formatDate(moment(start));
  };

  const formatEnd = (end: Date) => {
    return formatDate(moment(end));
  };

  const handleCustomTimerangeChange = (range: Timerange) => {
    const { start, end } = range;
    setTimerange({
      start: formatStart(start),
      end: formatEnd(end)
    });
    return Promise.resolve();
  };

  const allMenuItem = {
    label: `${props.allTitle ?? "Submission Time"}`,
    value: TimeEnum.ALL
  };

  const customDateMenuItem = {
    label: "Custom",
    value: TimeEnum.CUSTOM,
    content: ({ closeMenu }: { closeMenu: () => void }) => (
      <div className={"mb-10"}>
        <CustomTimerange
          start={timerange?.start ? moment(timerange?.start) : undefined}
          end={timerange?.end ? moment(timerange?.end) : undefined}
          type="time"
          onApply={(range: Timerange) => {
            handleCustomTimerangeChange(range).then(() => {
              closeMenu();
            });
          }}
        />
      </div>
    )
  };

  const menu = (props.getMenuItems ?? getMenuItems)(
    allMenuItem,
    customDateMenuItem
  ).filter(item => !(props.onlyPeriods && item.value === TimeEnum.ALL));

  const handlePeriodTimerangeChange = (filter: TimerangeFilter) => {
    const range = {
      start: utcTimestampFromString(filter.value),
      end: utcTimestampFromString("now")
    };
    setTimerange(range);
    props.onTimerangeChange(range, filter);
  };

  const handleTimerangeChange = (filter: TimerangeFilter) => {
    if (filter.value !== TimeEnum.CUSTOM) {
      handlePeriodTimerangeChange(filter);
      return;
    }
    props.onTimerangeChange(timerange as Timerange, filter);
  };

  const handleFilterChange = (filter: TimerangeFilter) => {
    if (props.onFilterChange) {
      props.onFilterChange(filter);
    }
    handleTimerangeChange(filter);
  };

  return (
    <DropdownFilter
      {...props}
      title={props.title ?? "Created"}
      field="created"
      menuItems={menu}
      styles={{
        button: {
          minWidth: "150px"
        },
        menu: {
          minWidth: "150px"
        },
        ...props.styles
      }}
      containerClassName={clsx("rdt__menu-container", props.className)}
      buttonClassname={clsx(props.buttonClassname, {
        "is-invalid": props.isInvalid
      })}
      CustomDropdownMenuComponent={TabDropdownMenu}
      onFilterChange={handleFilterChange}
      iconName={
        props.withoutIcon
          ? `${props.isSynced ? "cleared" : ""}`
          : "time-and-date"
      }
    />
  );
};

export default TimeRangeDropdownFilter;
